/**
 * This module provides utilities for validating document numbers and email addresses.
 */
import { IMask } from 'react-imask'

/**
 * Document Masks
 * This object contains masks and regular expressions for validating various document types.
 */
export const docMasks: {
  [k: string]: { mask: any; regexp: RegExp }
} = {
  CRM: {
    mask: [{ mask: '00000' }, { mask: '000000' }, { mask: '0000000' }],
    regexp: /^\d{5,7}[A-Z]{2}$/
  },
  COREN: {
    mask: [
      {
        mask: '00000T',
        blocks: {
          TASC: {
            mask: IMask.MaskedEnum,
            enum: ['T', 't', 'E', 'e']
          }
        }
      },
      {
        mask: '000000T',
        blocks: {
          T: {
            mask: IMask.MaskedEnum,
            enum: ['T', 't', 'E', 'e']
          }
        }
      },
      {
        mask: '0000000T',
        blocks: {
          T: {
            mask: IMask.MaskedEnum,
            enum: ['T', 't', 'E', 'e']
          }
        }
      }
    ],
    regexp: /^\d{5,7}([TE])?[A-Z]{2}$/
  },
  CREFITO: {
    mask: [
      {
        mask: 'CREFITO 0/00000',
        blocks: {
          '0': {
            mask: IMask.MaskedEnum,
            enum: ['1', '2', '3', '4', '5', '6', '7', '8', '9']
          },
          '00000': {
            mask: Number,
            min: 1,
            max: 99999
          }
        }
      }
    ],
    regexp: /^CREFITO [1-9]\/\d{1,5}[A-Z]{2}$/
  },
  NA: {
    mask: '',
    regexp: /.^/
  }
}

/**
 * Validates a field value against a specified mask.
 * @param value - The value to validate
 * @param mask - The regular expression mask to use for validation
 * @returns {boolean} - Returns true if the value is valid, false otherwise
 */
export const validateField = (value: string, mask: RegExp): boolean => {
  return mask.test(value)
}

/**
 * Validates an email address against a specified pattern.
 * @param email - The email address to validate
 * @returns {boolean} - Returns true if the email is valid, false otherwise
 */
export const validateEmail = (email: string): boolean => {
  return validateField(
    email,
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  )
}

/**
 * Formats a value according to a specified mask.
 * @param value - The value to format
 * @param mask - The mask to apply
 * @returns The formatted value
 */
export const formatMask = (value: string, mask: string | any) => {
  const masked = IMask.createMask({ mask })
  masked.resolve(String(value))

  return masked.value
}

/**
 * Formats a CNPJ number according to the Brazilian standard.
 * @param value - The CNPJ number to format
 * @returns The formatted CNPJ number
 */
export const formatCNPJ = (value: string) => {
  if (Number.isNaN(Number(value))) {
    return ''
  }

  return formatMask(value, '00.000.000/0000-00')
}
